import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { config } from 'config'
import { resizeGdbImage } from 'utils'
import { useFiltersContext } from 'context/filters-context'
import { useAppContext } from 'context/app-context'
import { Container, ContainerIntro } from 'components/container'
import PhotoPlaceholder from 'components/assets/nophoto.svg'
import { Map } from './map'
import { Filters } from './filters'
import { TextWithLinks } from '../detail/helpers'
import { Carousel } from './carousel'
import { Digits } from './digit/digits'
import {
  Intro,
  Main,
  NoResults,
  People,
  Title,
  FiltersTitle,
  FiltersIntro,
  ProfilePicture,
  Disclaimer,
  DisclaimerBackground,
  MapContainer,
  Summary,
  Magnify,
  Close,
  DisclaimerInner,
  CounterWrapper,
} from './styled'

const options = {
  root: null,
  rootMargin: '0px',
  threshold: 1,
}

const removeThirdWord = (_name: string | null) => {
  if (!_name) {
    return _name
  }

  let name = _name.trim()
  let lastIndex = name.lastIndexOf(' ')
  const regExp = /\s/g
  let allIndexes = [...name.matchAll(regExp)]
  let output = allIndexes.length > 1 ? name.substring(0, lastIndex) : name

  return output
}

export const Home = () => {
  const { strings } = useAppContext()
  const { data } = useAppContext()
  const { filteredData } = useFiltersContext()
  const mainRef = useRef<HTMLDivElement>(null)
  const [counterRef, setCounterRef] = useState<HTMLDivElement | null>(null)
  const [isCounterVisible, setCounterVisible] = useState(false)
  const [isMapVisible, setMapVisible] = useState(false)
  const [activeDisclaimer, setActiveDisclaimer] = useState(false)
  const [counterValue, setCounterValue] = useState(256)
  const handleRef = useCallback((ref: HTMLDivElement) => setCounterRef(ref), [])

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setCounterVisible(entry.isIntersecting)
    }, options)

    if (counterRef) {
      observer.observe(counterRef)
    }

    return () => {
      if (counterRef) {
        observer.disconnect()
      }
    }
  }, [counterRef])

  useEffect(() => {
    if (isCounterVisible) {
      setMapVisible(true)
      const timeoutId = setTimeout(() => setCounterValue(data?.length), 200)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [data, isCounterVisible, isMapVisible])

  useEffect(() => {
    setMapVisible(false)
    document.body.classList.remove('white')
  }, [])

  useEffect(() => {
    if (activeDisclaimer) {
      document.documentElement.classList.add('locked')
    } else {
      document.documentElement.classList.remove('locked')
    }
  }, [activeDisclaimer])

  return (
    <>
      <ContainerIntro lang={config.language}>
        <header>
          <Carousel />
          <Title>{strings.title}</Title>
          <Intro>{strings.intro}</Intro>
        </header>
      </ContainerIntro>

      <MapContainer>
        <Summary ref={handleRef}>
          {strings.filters_total_results_before}{' '}
          <CounterWrapper>
            <Digits places={4} value={counterValue} />
            {strings.filters_total_results_after}
          </CounterWrapper>
        </Summary>
        <Map
          isVisible={isMapVisible}
          onInfo={() => {
            setActiveDisclaimer(true)
          }}
        />
        <Disclaimer $active={activeDisclaimer}>
          <DisclaimerBackground
            onClick={() => {
              setActiveDisclaimer(false)
            }}
          />
          <DisclaimerInner>
            <Close
              onClick={() => {
                setActiveDisclaimer(false)
              }}
            >
              ×
            </Close>
            <TextWithLinks text={strings.map_disclaimer} />
          </DisclaimerInner>
        </Disclaimer>
      </MapContainer>

      <Container lang={config.language}>
        <Main ref={mainRef}>
          <FiltersIntro>
            <FiltersTitle>{strings.filters_title}</FiltersTitle>
            <p>{strings.filters_description}</p>
          </FiltersIntro>
          <Filters />

          {filteredData?.length ? (
            <People>
              {filteredData?.map(item => (
                <li key={item.id}>
                  <Link to={`/detail/${item.id}`}>
                    <ProfilePicture
                      src={resizeGdbImage(
                        item.main_photo_url || PhotoPlaceholder,
                        256,
                        undefined,
                        { _r: 5 }
                      )}
                      alt={item.name}
                    />
                    {removeThirdWord(item.name)}
                  </Link>
                  <Magnify />
                </li>
              ))}
            </People>
          ) : (
            <NoResults>{strings.filters_no_results}</NoResults>
          )}
        </Main>
      </Container>
    </>
  )
}
